import { isEquipmentBased } from "common";
import { produce } from "immer";
import { sortBy } from "lodash";
import {
    AllowedPrivacy,
    DemographicPricingGroup,
    EqualsFilter,
    Experience,
    GreaterThanOrEqualsToFilter,
    InProductScheduleFilter,
    LesserThanOrEqualsToFilter,
    LineItemTemplate,
    PricingGroup,
    PricingStructure,
    PricingTerm,
    PurchaseRule,
    PurchaseRuleFilterObject,
    PurchaseRuleFilterOperator,
    PurchaseRuleLink,
} from "types";
import { isOptionallyPrivateExperience } from "./Experience";
import {
    generatePricingGroupFromPurchaseRules,
    generatePurchaseRuleActionItems,
    getDefaultPricingTerm,
    getPricingStructureFromPurchaseRuleTags,
} from "./PricingGroup";
import { getPurchaseRulesByTag, getTagIdsFromPurchaseRule } from "./PurchaseRule";
import { getPurchaseRulesFromPurchaseRuleLink } from "./PurchaseRuleLink";

/**
 *  Generates Demographic Pricing Group Model from all purchaseRules for a particular privacy.
 *  This function uses generatePricingGroupFromPurchaseRule function from `PricingGroup` model which is
 *  a base model.
 */
export const generateDemographicPricingGroupFromPurchaseRules = (
    purchaseRules?: PurchaseRule[],
    privacy: AllowedPrivacy = AllowedPrivacy.PUBLIC,
): DemographicPricingGroup => {
    const pricingGroup: PricingGroup = generatePricingGroupFromPurchaseRules(purchaseRules, privacy);
    const filteredPurchaseRules = getPurchaseRulesByTag(privacy, purchaseRules);
    const purchaseRuleTags =
        filteredPurchaseRules.length > 0 ? getTagIdsFromPurchaseRule(filteredPurchaseRules[0]) : [];

    const demographicPricingGroup: DemographicPricingGroup = {
        ...pricingGroup,
        pricingStructure: getPricingStructureFromPurchaseRuleTags(purchaseRuleTags),
        schedules: getProductSchedulesFromPurchaseRule(filteredPurchaseRules[0]),
    };

    /**
     * Returning default value as first term only for flat since tiered pricing has add tier button
     * and we don't need to show the default value table
     */
    if (
        demographicPricingGroup.terms.length === 0 &&
        demographicPricingGroup.pricingStructure === PricingStructure.FLAT
    ) {
        demographicPricingGroup.terms.push(getDefaultPricingTerm());
    }

    return demographicPricingGroup;
};

/**
 * Returns selected Schedules from a purchase rule. If not found we will return default value
 */
export const getProductSchedulesFromPurchaseRule = (purchaseRule: PurchaseRule) => {
    /**
     * This function is specific used for pricing rules. can be made generic
     */
    const defaultSchedules = { all: true, items: [] };

    if (!purchaseRule || purchaseRule.filter?.object !== "and_filter") {
        return defaultSchedules;
    }

    const inProductScheduleFilter = purchaseRule.filter?.filters.find((filter) => {
        return filter.object === PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER;
    }) as InProductScheduleFilter;

    return inProductScheduleFilter?.schedules ?? defaultSchedules;
};

/**
 * Generates Purchase Rules for an Experience. Experience can have either private or public pricing or both.
 *
 * Instead of creating new purchase rules every time. We will use the existing purchase rules and update them.
 * Based on Changes we may also need to create new purchase rules and also if we remove any tiers we may need
 * to remove the purchase rules. This function will return the set of purchase rules based on updates or
 * deletions.
 *
 * 1. we will check if experience has both privacies, then will use respective privacy purchase rules and update them
 * 2. If we have only one privacy, we will sort the purchase rules such that we will use the selected privacy rules
 * first and then we will use the other privacy rules for updates.
 */
export const generatePurchaseRulesFromDemographicPricingGroup = (
    experience: Experience,
    lineItemTemplates: LineItemTemplate[],
    purchaseRuleLink?: PurchaseRuleLink,
    publicDemographicPricingGroup?: DemographicPricingGroup,
    privateDemographicPricingGroup?: DemographicPricingGroup,
): { updates: PurchaseRule[]; deletions: PurchaseRule[] } => {
    let updatedPurchaseRules: PurchaseRule[] = [];
    let deletedPurchaseRules: PurchaseRule[] = [];
    const pricingGroupMap = {
        [AllowedPrivacy.PRIVATE]: privateDemographicPricingGroup,
        [AllowedPrivacy.PUBLIC]: publicDemographicPricingGroup,
    };

    const existingPurchaseRules = getPurchaseRulesFromPurchaseRuleLink(purchaseRuleLink).filter((purchaseRule) => {
        return !purchaseRule.deletedAt;
    });

    if (experience.allowedPrivacies.length > 1) {
        /**
         * we will create/update Purchase rules for both privacies. If existing rules are there for
         * respective privacy, we will use them.
         *
         * we are creating public rules first and then creating private first to ensure public rules get less sequence
         */
        for (const allowedPrivacy of [AllowedPrivacy.PUBLIC, AllowedPrivacy.PRIVATE]) {
            const existingPrivacyPurchaseRules = getPurchaseRulesByTag(allowedPrivacy, existingPurchaseRules);
            const pricingGroup = pricingGroupMap[allowedPrivacy]!;
            const purchaseRuleChanges = createOrUpdatePurchaseRulesForAllowedPrivacy(
                experience,
                lineItemTemplates,
                existingPrivacyPurchaseRules,
                pricingGroup,
            );
            updatedPurchaseRules = [...updatedPurchaseRules, ...purchaseRuleChanges.updates];
            deletedPurchaseRules = [...deletedPurchaseRules, ...purchaseRuleChanges.deletions];
        }
    } else {
        /**
         * Only one privacy exists for the experience. we will update same privacy purchase rules first
         * and then we will use other privacy rules for generating new purchase rules.
         */
        const allowedPrivacy = experience.allowedPrivacies[0];
        const pricingGroup = pricingGroupMap[allowedPrivacy];
        const existingPurchaseRulesMap = {
            [AllowedPrivacy.PUBLIC]: getPurchaseRulesByTag(AllowedPrivacy.PUBLIC, existingPurchaseRules),
            [AllowedPrivacy.PRIVATE]: getPurchaseRulesByTag(AllowedPrivacy.PRIVATE, existingPurchaseRules),
        };
        const reArrangedPurchaseRules =
            allowedPrivacy === AllowedPrivacy.PRIVATE
                ? [
                      ...existingPurchaseRulesMap[AllowedPrivacy.PRIVATE],
                      ...existingPurchaseRulesMap[AllowedPrivacy.PUBLIC],
                  ]
                : [
                      ...existingPurchaseRulesMap[AllowedPrivacy.PUBLIC],
                      ...existingPurchaseRulesMap[AllowedPrivacy.PRIVATE],
                  ];
        const purchaseRuleChanges = createOrUpdatePurchaseRulesForAllowedPrivacy(
            experience,
            lineItemTemplates,
            reArrangedPurchaseRules,
            pricingGroup!,
        );
        updatedPurchaseRules = [...updatedPurchaseRules, ...purchaseRuleChanges.updates];
        deletedPurchaseRules = [...deletedPurchaseRules, ...purchaseRuleChanges.deletions];
    }

    return { updates: updatedPurchaseRules, deletions: deletedPurchaseRules };
};

/**
 * Creates Purchase Rules for a Particular Privacy.
 *
 * Process of creation
 *
 * 1. Check if there are existing purchase rules for the given privacy, if no then we will generate purchase rules based on values
 * 2. If there are existing purchase rules
 *
 *    a) If the pricing structure is FLAT, then we will update the latest values into first purchase rule since flat is a
 *  base pricing and have only one purchase rule. we will remove any extra purchase rules
 *
 *    b) If the pricing structure is TIERED, then we will sort the existing purchase rules by min value, start updating the
 * existing rules with latest values.
 */
const createOrUpdatePurchaseRulesForAllowedPrivacy = (
    experience: Experience,
    lineItemTemplates: LineItemTemplate[],
    existingPurchaseRules: PurchaseRule[],
    demographicPricingGroup: DemographicPricingGroup,
): { updates: PurchaseRule[]; deletions: PurchaseRule[] } => {
    const updatedPurchaseRules = [];
    const deletedPurchaseRules = [];

    if (existingPurchaseRules.length > 0) {
        if (
            [PricingStructure.FLAT, PricingStructure.RELATIVE, PricingStructure.BUYOUT].includes(
                demographicPricingGroup?.pricingStructure,
            )
        ) {
            const updatedPurchaseRule = createOrUpdatePurchaseRuleFromDemographicPricingGroup(
                experience,
                lineItemTemplates,
                demographicPricingGroup,
                demographicPricingGroup.terms[0],
                existingPurchaseRules[0],
            );
            updatedPurchaseRules.push(updatedPurchaseRule);

            for (let index = 1; index < existingPurchaseRules.length; index++) {
                deletedPurchaseRules.push(existingPurchaseRules[index]);
            }
        } else {
            // Tiered Pricing Structure. will have multiple rules
            // Sort them by meta.min and assign the value
            const sortedPurchaseRules = sortBy(existingPurchaseRules, "meta.tieredPricing.min");

            for (let index = 0; index < demographicPricingGroup.terms.length; index++) {
                const updatedPurchaseRule = createOrUpdatePurchaseRuleFromDemographicPricingGroup(
                    experience,
                    lineItemTemplates,
                    demographicPricingGroup,
                    demographicPricingGroup.terms[index],
                    index < sortedPurchaseRules.length ? sortedPurchaseRules[index] : undefined,
                );
                updatedPurchaseRules.push(updatedPurchaseRule);
            }

            for (let index = demographicPricingGroup.terms.length; index < sortedPurchaseRules.length; index++) {
                deletedPurchaseRules.push(sortedPurchaseRules[index]);
            }
        }
    } else {
        // No Rules Exist. we will create new PurchaseRules now
        // Created as many rules as tiers (one for flat, n for tiered)
        for (const pricingTerm of demographicPricingGroup.terms) {
            updatedPurchaseRules.push(
                createOrUpdatePurchaseRuleFromDemographicPricingGroup(
                    experience,
                    lineItemTemplates,
                    demographicPricingGroup,
                    pricingTerm,
                ),
            );
        }
    }

    return { updates: updatedPurchaseRules, deletions: deletedPurchaseRules };
};

/**
 *  Creates Purchase Rules from Demographic Pricing Group
 */
const createOrUpdatePurchaseRuleFromDemographicPricingGroup = (
    experience: Experience,
    lineItemTemplates: LineItemTemplate[],
    pricingGroup: DemographicPricingGroup,
    pricingTerm: PricingTerm,
    purchaseRule?: PurchaseRule,
): PurchaseRule => {
    const updatedPurchaseRule: any = purchaseRule ?? {};
    updatedPurchaseRule.name = generatePurchaseRuleName(experience.name, pricingGroup, pricingTerm);
    updatedPurchaseRule.filter = generatePurchaseRuleFilter(experience, pricingGroup, pricingTerm) ?? null;

    updatedPurchaseRule.actions = generatePurchaseRuleActionItems(
        lineItemTemplates,
        pricingGroup,
        pricingTerm,
        purchaseRule,
        isEquipmentBased(experience),
    );

    updatedPurchaseRule.seller = { id: experience.seller.id };
    updatedPurchaseRule.tags = [
        { id: pricingGroup.privacy },
        { id: pricingGroup.pricingType },
        { id: pricingGroup.pricingStructure },
    ];
    updatedPurchaseRule.meta = generatePurchaseRuleMeta(pricingGroup, pricingTerm);
    return updatedPurchaseRule as PurchaseRule;
};

/**
 * Generates Purchase Rule Name based on PricingGroup values
 */
const generatePurchaseRuleName = (
    experienceName: string,
    pricingGroup: DemographicPricingGroup,
    pricingTerm: PricingTerm,
): string => {
    const privacyString = pricingGroup.privacy === AllowedPrivacy.PRIVATE ? "Private " : "";
    let structureString = "";

    switch (pricingGroup.pricingStructure) {
        case PricingStructure.FLAT: {
            structureString = pricingGroup.privacy === AllowedPrivacy.PUBLIC ? "Base" : "";
            break;
        }

        case PricingStructure.RELATIVE: {
            structureString = "Relative";
            break;
        }

        case PricingStructure.TIERED: {
            structureString = "Tiered";
            break;
        }

        case PricingStructure.BUYOUT: {
            structureString = "Buyout";
            break;
        }

        default: {
            structureString = "";
        }
    }

    let name = `${
        pricingGroup.pricingStructure === PricingStructure.FLAT ? privacyString : ""
    }${structureString} Purchase Rule for ${experienceName}`;

    if (pricingGroup.pricingStructure === PricingStructure.TIERED && pricingTerm.quantityMin) {
        const quantityMaxString = pricingTerm.quantityMax ? `-${pricingTerm.quantityMax}` : "+";
        name += `: Quantity ${pricingTerm.quantityMin}${quantityMaxString} (${pricingGroup.privacy})`;
    }

    return name;
};

/**
 * Creates Meta object for purchase rule based on pricing group and pricing term
 * we need to have meta only for tiered pricing purchase rules
 */
const generatePurchaseRuleMeta = (pricingGroup: DemographicPricingGroup, pricingTerm: PricingTerm) => {
    const meta: any = {};
    if (pricingGroup.pricingStructure === PricingStructure.TIERED) {
        meta.tieredPricing = {
            min: pricingTerm.quantityMin,
            ...(pricingTerm.quantityMax && { max: pricingTerm.quantityMax }),
        };
    }

    return meta;
};

/**
 * Creates Purchase Rule Filters based on pricing group values.
 * This function can also be used by pricing Group
 */
export const generatePurchaseRuleFilter = (
    experience: Experience,
    pricingGroup: PricingGroup | DemographicPricingGroup,
    pricingTerm: PricingTerm,
) => {
    const purchaseRuleFilters: Array<
        EqualsFilter | InProductScheduleFilter | GreaterThanOrEqualsToFilter | LesserThanOrEqualsToFilter
    > = [];

    const { allowedPrivacies } = experience;

    const manualTimeslotResolution =
        "schedules" in pricingGroup &&
        pricingGroup.schedules.items.length > 0 &&
        isOptionallyPrivateExperience(experience);

    if (allowedPrivacies.length > 1 && pricingGroup.privacy === AllowedPrivacy.PRIVATE) {
        // Privacy will not be added for public rules, since we want them to apply for unscheduled experience
        purchaseRuleFilters.push({
            field: "privacy",
            object: PurchaseRuleFilterObject.EQUALS_FILTER,
            operator: PurchaseRuleFilterOperator.EQUALS,
            operand: pricingGroup.privacy,
        });

        if ("schedules" in pricingGroup && (pricingGroup.schedules?.all || pricingGroup.schedules.items.length > 0)) {
            const { items } = pricingGroup.schedules;
            const experienceScheduleIds = new Set(experience.schedules.map((schedule) => schedule.id ?? ""));
            const filteredItems = items.length > 0 ? items.filter((item) => experienceScheduleIds.has(item)) : items;
            const schedules = { ...pricingGroup.schedules, items: filteredItems };

            purchaseRuleFilters.push({
                object: PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER,
                operator: PurchaseRuleFilterOperator.IN_PRODUCT_SCHEDULE,
                product: { id: experience.id },
                schedules,
                manualTimeslotResolution,
            });
        }
    }

    if ("pricingStructure" in pricingGroup && pricingGroup.pricingStructure === PricingStructure.TIERED) {
        purchaseRuleFilters.push({
            object: PurchaseRuleFilterObject.GREATER_THAN_OR_EQUAL_TO_FILTER,
            operator: PurchaseRuleFilterOperator.GTE,
            field: "quantity",
            operand: pricingTerm.quantityMin ?? 1,
        });

        /**
         * LTE Filter should not apply for first tier since its a base price and this prices
         * should apply be default to all tiers.
         */
        if (pricingTerm.quantityMin !== 1 && pricingTerm.quantityMax) {
            purchaseRuleFilters.push({
                object: PurchaseRuleFilterObject.LESSER_THAN_OR_EQUAL_TO_FILTER,
                operator: PurchaseRuleFilterOperator.LTE,
                field: "quantity",
                operand: pricingTerm.quantityMax,
            });
        }
    }

    if (purchaseRuleFilters.length > 1) {
        return {
            object: PurchaseRuleFilterObject.AND_FILTER,
            operator: PurchaseRuleFilterOperator.AND,
            filters: purchaseRuleFilters,
        };
    }

    return purchaseRuleFilters[0];
};

/**
 * Pricing Groups will always have one term because we need to show empty vales for flat pricing
 * so when we toggle to tiered pricing, we are checking even if tier exist, are there any demographic
 * prices in it.
 */
export const hasTerms = (pricingGroup: PricingGroup): boolean => {
    if (!pricingGroup.terms || pricingGroup.terms.length === 0) {
        return false;
    }

    return pricingGroup.terms[0].demographicPrices?.length > 0;
};

export const mergePricingTypeAndTerms = (
    currentPricingGroup: DemographicPricingGroup,
    overridingPricingGroup: DemographicPricingGroup,
): DemographicPricingGroup => {
    const updatedPricingGroup = produce(currentPricingGroup, (draft) => {
        draft.terms = [...overridingPricingGroup.terms];
        draft.pricingType = overridingPricingGroup.pricingType;
        draft.pricingStructure = overridingPricingGroup.pricingStructure;
    });

    return updatedPricingGroup;
};
